
import Vue from 'vue'
import AppMixin from '@/_mixins/app'
import { getObjectDifferenceInArray, getObjectDifferenceInObject } from '@/utils/utils'
import { getRequest, patchRequest } from '@/plugins/api-axios'
import { getRequestHeaders, getURL, notificationBaseUrl } from '@/utils/axios'
import { deserialise, serialise } from 'kitsu-core'
import { groupBy } from 'lodash'
import { NotificationType, Project, UserSettings } from '@/views/Notification/types/notification'
import ProjectAutocomplete from '@/views/Project/components/projectAutocomplete.vue'
import SelectWeekday from '@/components/Select/Weekday.vue'
import TimePicker from '@/components/Picker/Time.vue'

const UserNotificationsSettings = Vue.extend({
    mixins: [AppMixin],
    components: { ProjectAutocomplete, SelectWeekday, TimePicker },
    props: {},

    data() {
        return {
            notificationProject: null as null | Project,
            initializingUserSettings: false, //loding User settings from BE, showing card progress bar
            userSettingsChanges: [] as Array<string>, //Arra of changed fields in User settings, help us show loading on individual fields
            userSettings: null as null | UserSettings,
            initializingNotificationTypes: false,
            notificationTypes: [] as Array<NotificationType>,
            transformedNotifications: {} as Record<string, Array<NotificationType>>,
            initializingNotificationUserSettings: false,
            disable: false,
            notificationUserSettings: null,
            notificationHeaders: [
                { text: '', align: 'start', value: 'eventName' },
                { text: 'web', align: 'center', value: 'web' },
                { text: 'email', align: 'center', value: 'email' },
                { text: 'sms', align: 'center', value: 'sms' },
            ],
            projectSettingsPanel: [],

            webNotifications: ['inherit', 'off', 'on'],
            emailNotifications: ['inherit', 'disabled', 'immediate', 'daily', 'weekly'],
            smsNotifications: ['inherit', 'off', 'on'],
        }
    },
    computed: {
        computedUserSettings() {
            return Object.assign({}, this.userSettings)
        },
        patchingUserSettings() {
            return !!this.userSettingsChanges.length
        },
    },

    methods: {
        isHelpTextAvailable(item: any) {
            const translateString = `notification_types_${item.sourceService}.${item.eventName}${
                item.forModuleId ? '.' + item.forModuleId : ''
            }${item.forModulePart ? '.' + item.forModulePart : ''}.help`
            const translatedValue = this.getComplexTranslations(translateString)
            if (
                translatedValue == translateString ||
                translatedValue ==
                    `${item.eventName}${item.forModuleId ? '.' + item.forModuleId : ''}${
                        item.forModulePart ? '.' + item.forModulePart : ''
                    }.help`
            ) {
                return false
            }
            return true
        },
        loadingUserSettingsField(fieldName: string) {
            return this.initializingUserSettings && this.userSettingsChanges.includes(fieldName)
        },
        getInheritOverride(notificationType, type: 'web' | 'email' | 'sms') {
            let result: any = null

            switch (type) {
                case 'web':
                    result = notificationType.webDefault
                    break
                case 'email':
                    result = notificationType.emailDefault
                    break
                case 'sms':
                    result = notificationType.smsDefault
                    break
            }

            if (notificationType.userOverrides.data.length > 0) {
                let projectlessSettings = notificationType.userOverrides.data.find(
                    (item) => !item.project.data
                )
                if (projectlessSettings) {
                    if (
                        projectlessSettings[type] !== 'inherit' &&
                        result !== 'forced' &&
                        this.notificationProject
                    ) {
                        result = projectlessSettings[type]
                    }
                }
            }

            return result
        },
        getOverride(notificationType) {
            let result: any = null
            if (notificationType.userOverrides.data.length > 0) {
                notificationType.userOverrides.data.map((setting: any) => {
                    if (this.notificationProject) {
                        if (this.notificationProject == setting.project?.data?.id) {
                            result = setting
                        }
                    } else {
                        if (!setting.project.data) {
                            result = setting
                        }
                    }
                })
            }
            return result
        },

        getWeb(notificationType) {
            let override = this.getOverride(notificationType)

            let result = 'inherit'
            if (notificationType.webDefault == 'forced') {
                result = 'forced'
            } else if (override) {
                result = override.web
            }

            return result
        },

        getEmail(notificationType) {
            let override = this.getOverride(notificationType)
            let result = 'inherit'
            if (override) {
                result = override.email
            }
            return result
        },

        getSms(notificationType) {
            let override = this.getOverride(notificationType)

            let result = 'inherit'
            if (notificationType.smsDefault == 'forced') {
                result = 'forced'
            } else if (override && notificationType.smsDefault !== 'unsupported') {
                result = override.sms
            }

            return result
        },

        async update(notificationType: any, type: string, event: any) {
            this.disable = true
            let userSettings: any = null
            let override = this.getOverride(notificationType)

            if (override) {
                userSettings = override
            } else {
                let res = await this.searchNotificationUserSettings({
                    'filter[notificationType]': notificationType.id,
                    ...(this.notificationProject && {
                        'filter[project]': this.notificationProject,
                    }),
                })
                userSettings = res[0]
            }

            if (userSettings.id) {
                let params = {
                    id: userSettings.id,
                    type: userSettings.type,
                    attributes: {
                        ...(type == 'web' && { web: event }),
                        ...(type == 'email' && { email: event }),
                        ...(type == 'sms' && { sms: event }),
                    },
                }
                try {
                    const result = await this.patchNotificationUserSettings(params)
                    const index = notificationType.userOverrides.data.findIndex(
                        (item) => item.id == result.id
                    )
                    if (index > -1) {
                        notificationType.userOverrides.data[index] = result
                    } else {
                        notificationType.userOverrides.data.push(result)
                    }
                } catch (error) {
                    //
                } finally {
                    this.disable = false
                }
            }
        },

        async searchNotificationUserSettings(params) {
            try {
                let { data } = await getRequest(
                    getURL({
                        path: `notificationUserSettings`,
                        base: notificationBaseUrl,
                    }),
                    getRequestHeaders('GET'),
                    {
                        include: 'project',
                        ...params,
                    }
                )
                return deserialise(data).data
            } catch (error) {
                //
            } finally {
                this.initializingNotificationTypes = false
            }
        },

        async patchNotificationUserSettings(params: any) {
            try {
                let { data } = await patchRequest(
                    getURL({
                        path: `notificationUserSettings/${params.id}`,
                        base: notificationBaseUrl,
                    }),
                    getRequestHeaders('PATCH'),
                    {
                        include: 'project',
                        ...params,
                    }
                )
                return deserialise(data).data
            } catch (error) {
                //
            } finally {
                this.initializingNotificationTypes = false
            }
        },

        async getUserSettings() {
            if (this.currentUser && this.currentUser.id) {
                this.initializingUserSettings = true
                try {
                    let { data } = await getRequest(
                        getURL({
                            path: `userSettings/${this.currentUser.id}`,
                            base: notificationBaseUrl,
                        }),
                        getRequestHeaders('GET')
                    )
                    this.userSettings = deserialise(data).data
                } catch (error) {
                    //
                } finally {
                    this.initializingUserSettings = false
                }
            }
        },

        async patchUserSettings(newVal, oldVal) {
            if (this.userSettings) {
                this.userSettingsChanges = getObjectDifferenceInArray(newVal, oldVal)
                if (this.patchingUserSettings) {
                    let payload = serialise(this.userSettings.type, {
                        id: this.userSettings.id,
                        ...getObjectDifferenceInObject(newVal, oldVal),
                    })
                    try {
                        let { data } = await patchRequest(
                            this.userSettings.links.self,
                            getRequestHeaders('PATCH'),
                            payload.data
                        )
                        this.userSettings = deserialise(data).data
                    } catch (error) {
                        //
                    } finally {
                        this.userSettingsChanges = []
                    }
                }
            }
        },

        async getNotificationTypes() {
            if (this.currentUser && this.currentUser.id) {
                this.initializingNotificationTypes = true
                try {
                    let { data } = await getRequest(
                        getURL({
                            path: `notificationTypes`,
                            base: notificationBaseUrl,
                        }),
                        getRequestHeaders('GET'),
                        {
                            include: 'userOverrides.project',
                        }
                    )
                    const included = deserialise({
                        data: data.included,
                        included: data.included,
                    }).data

                    this.notificationTypes = deserialise({
                        data: data.data,
                        included: included,
                    }).data
                    // filter out notification type With the same sourceService and eventName (ignoring any forModuleId and forModulePart set on the resource)
                    const notificationListGroupedBySourceService = this.notificationTypes.filter(
                        (item) => !item.forModuleId && !item.forModulePart
                    )
                    // filter out notification type With the same sourceService and eventName, associated with a resource with the same forModuleId (ignoring any forModulePart set on the resource)
                    const notificationListGroupedByForModuleId = this.notificationTypes.filter(
                        (item) => item.forModuleId && !item.forModulePart
                    )
                    // filter out notification type With the same sourceService and eventName, associated with a resource with the same forModuleId and forModulePart
                    const notificationListGroupedByForModuleIdAndForModulePart =
                        this.notificationTypes.filter(
                            (item) => item.forModuleId && item.forModulePart
                        )

                    this.transformedNotifications = {
                        ...groupBy(notificationListGroupedBySourceService, 'sourceService'),
                        ...groupBy(notificationListGroupedByForModuleId, 'forModuleId'),
                        ...groupBy(
                            notificationListGroupedByForModuleIdAndForModulePart,
                            'forModuleId'
                        ),
                    }
                    // remove Documents and Tasks since those modules not ready yet
                    delete this.transformedNotifications['document']
                    delete this.transformedNotifications['task']
                } catch (error) {
                    //
                } finally {
                    this.initializingNotificationTypes = false
                }
            }
        },

        async getNotificationUserSettings() {
            if (this.currentUser && this.currentUser.id) {
                this.initializingUserSettings = true
                try {
                    let { data } = await getRequest(
                        getURL({
                            path: `userSettings/${this.currentUser.id}`,
                            base: notificationBaseUrl,
                        }),
                        getRequestHeaders('GET')
                    )
                    this.userSettings = deserialise(data).data
                } catch (error) {
                    //
                } finally {
                    this.initializingUserSettings = false
                }
            }
        },
    },

    async mounted() {
        await this.getUserSettings()
        await this.getNotificationTypes()
    },

    watch: {
        computedUserSettings: {
            immediate: false,
            deep: true,
            async handler(newVal, oldVal) {
                await this.patchUserSettings(newVal, oldVal)
            },
        },
    },
})

export default UserNotificationsSettings
